import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  IconCheck,
  IconLock,
  IconAlertTriangle,
} from "@tabler/icons-react";
import classes from "./partnerLogin.module.css";
import { Logo } from "../../constants";
import {
  partnerLogin,
  partnerForgotPassword,
  getPartnerAuthError,
  setPartnerSession,
} from "../../services/partnerCalls";

const HIGHLIGHTS = [
  "58 role-calibrated assessments over one API",
  "Candidate-fit scoring across your whole pool",
  "Real-time results by signed webhook",
];

const PartnerLogin = () => {
  const navigate = useNavigate();

  // Sign-in state
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [keepSignedIn, setKeepSignedIn] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState("");

  // Forgot-password state (inline view swap, same form shell)
  const [forgotMode, setForgotMode] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotSubmitting, setForgotSubmitting] = useState(false);
  const [forgotError, setForgotError] = useState("");
  const [forgotSent, setForgotSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setFormError("");
    setSubmitting(true);
    try {
      const res = await partnerLogin({ email, password, rememberMe: keepSignedIn });
      const client = res?.data?.data?.client;
      const token = res?.data?.data?.token;
      setPartnerSession(token, client, keepSignedIn);

      navigate("/partner/dashboard");
    } catch (err) {
      setFormError(getPartnerAuthError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const openForgotPassword = () => {
    setForgotError("");
    setForgotSent(false);
    setForgotEmail(email);
    setForgotMode(true);
  };

  const backToSignIn = () => {
    setForgotMode(false);
    setForgotError("");
    setForgotSent(false);
  };

  const handleForgotSubmit = async (e) => {
    e.preventDefault();
    if (forgotSubmitting) return;
    setForgotError("");
    setForgotSubmitting(true);
    try {
      await partnerForgotPassword({ email: forgotEmail });
      setForgotSent(true);
    } catch (err) {
      setForgotError(getPartnerAuthError(err));
    } finally {
      setForgotSubmitting(false);
    }
  };

  return (
    <div className={classes.page}>
      {/* BRAND SIDE */}
      <div className={classes.brandSide}>
        <span className={classes.brandGlow} />

        <Link to="/api-integrations" className={classes.brandLogo}>
          <Logo color="#F1F5FF" />
          <span className={classes.portalTag}>PARTNER PORTAL</span>
        </Link>

        <div className={classes.brandBody}>
          <div className={classes.brandHeadline}>
            Your assessment engine, behind your brand.
          </div>
          <div className={classes.brandSub}>
            Sign in to send assessments, score candidate pools by fit, and
            manage your API keys — all white-labelled.
          </div>
          <div className={classes.brandList}>
            {HIGHLIGHTS?.map((item) => (
              <div key={item} className={classes.brandItem}>
                <span className={classes.brandTick}>
                  <IconCheck size={11} stroke={3} />
                </span>
                {item}
              </div>
            ))}
          </div>
        </div>

        <div className={classes.brandFoot}>
          © {new Date().getFullYear()} Awasource · Partner API console
        </div>
      </div>

      {/* FORM SIDE */}
      <div className={classes.formSide}>
        {!forgotMode ? (
          <form className={classes.form} onSubmit={handleSubmit}>
            <div className={classes.formTitle}>Partner sign in</div>
            <div className={classes.formSub}>
              Welcome back. Sign in to your console.
            </div>

            {formError && (
              <div className={classes.errorBanner} role="alert">
                <IconAlertTriangle size={15} />
                <span>{formError}</span>
              </div>
            )}

            <div className={classes.field}>
              <label className={classes.label} htmlFor="partnerEmail">
                Work email
              </label>
              <input
                id="partnerEmail"
                className={classes.input}
                type="email"
                required
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
                disabled={submitting}
              />
            </div>

            <div className={classes.fieldLast}>
              <label className={classes.label} htmlFor="partnerPassword">
                Password
              </label>
              <input
                id="partnerPassword"
                className={classes.input}
                type="password"
                required
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••••"
                disabled={submitting}
              />
            </div>

            <div className={classes.formRow}>
              <label className={classes.checkLabel}>
                <input
                  type="checkbox"
                  checked={keepSignedIn}
                  onChange={(e) => setKeepSignedIn(e.target.checked)}
                  disabled={submitting}
                />
                Keep me signed in
              </label>
              <button
                type="button"
                className={classes.link}
                onClick={openForgotPassword}
                disabled={submitting}
              >
                Forgot password?
              </button>
            </div>

            <button
              type="submit"
              className={classes.submit}
              disabled={submitting}
            >
              {submitting ? "Signing in…" : "Sign in →"}
            </button>

            <div className={classes.footNote}>
              Access is login-only — Awasource issues partner credentials once
              your deal is signed.{" "}
              <Link to="/api-integrations#contact" className={classes.link}>
                Contact partnerships
              </Link>
            </div>

            <div className={classes.secureNote}>
              <span className={classes.lockIcon}>
                <IconLock size={12} />
              </span>
              Encrypted connection · login-only access
            </div>
          </form>
        ) : (
          <form className={classes.form} onSubmit={handleForgotSubmit}>
            <div className={classes.formTitle}>Reset your password</div>
            <div className={classes.formSub}>
              {forgotSent
                ? "Check your inbox for a reset link."
                : "Enter your work email and we'll send you a reset link."}
            </div>

            {forgotError && (
              <div className={classes.errorBanner} role="alert">
                <IconAlertTriangle size={15} />
                <span>{forgotError}</span>
              </div>
            )}

            {forgotSent ? (
              <div className={classes.successBanner}>
                <IconCheck size={15} />
                <span>
                  If an account exists for {forgotEmail}, a reset link has been
                  sent.
                </span>
              </div>
            ) : (
              <div className={classes.fieldLast}>
                <label className={classes.label} htmlFor="forgotEmail">
                  Work email
                </label>
                <input
                  id="forgotEmail"
                  className={classes.input}
                  type="email"
                  required
                  autoComplete="email"
                  value={forgotEmail}
                  onChange={(e) => setForgotEmail(e.target.value)}
                  placeholder="you@company.com"
                  disabled={forgotSubmitting}
                />
              </div>
            )}

            {!forgotSent && (
              <button
                type="submit"
                className={classes.submit}
                disabled={forgotSubmitting}
              >
                {forgotSubmitting ? "Sending…" : "Send reset link"}
              </button>
            )}

            <button
              type="button"
              className={classes.link}
              onClick={backToSignIn}
              style={{ marginTop: forgotSent ? "1rem" : undefined }}
            >
              ← Back to sign in
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default PartnerLogin;
